"use client"
import { createContext, useCallback, useMemo, useState } from "react"
import type React from "react"
import { Box, Typography, IconButton } from "@mui/material"
import { Iconify } from "@/components/common/iconify"
import { UI_ICONS } from "@/lib/ui-icons"
import { AnimatePresence, m as motion } from "framer-motion"

type ToastVariant = "success" | "warning" | "danger" | "info"

type ToastType = {
  id: number
  msg: string
  title: string
  variant: ToastVariant
  undoFn?: () => void
}

export interface ToastContextValue {
  toast: (msg: string, variant: string, title?: string) => void
  success: (msg: string, title?: string) => void
  error: (msg: string, title?: string) => void
  warning: (msg: string, title?: string) => void
  info: (msg: string, title?: string) => void
  withUndo: (msg: string, onUndo: () => void, opts?: { title?: string }) => void
}

const BASE_TOAST_CONFIG: ToastContextValue = {
  toast(msg, variant, title) {
    alert(`[${variant}] ${title ? title + ": " : ""}${msg}`)
  },
  success(msg, title) { this.toast(msg, "success", title || "Success") },
  error(msg, title) { this.toast(msg, "danger", title || "Error") },
  warning(msg, title) { this.toast(msg, "warning", title || "Warning") },
  info(msg, title) { this.toast(msg, "info", title || "Info") },
  withUndo(msg, _onUndo, opts) { this.toast(msg, "info", opts?.title || "Done") },
}

export const ToastContext = createContext<ToastContextValue>(BASE_TOAST_CONFIG)

const VARIANT_CONFIG: Record<ToastVariant, { color: string; icon: React.ReactNode; title: string }> = {
  success: { color: "var(--success-text)", icon: <Iconify icon={UI_ICONS.checkCircleFilled} width={20} />, title: "Success" },
  warning: { color: "var(--warning-text)", icon: <Iconify icon={UI_ICONS.warning} width={20} />, title: "Warning" },
  danger:  { color: "var(--danger-text)",  icon: <Iconify icon={UI_ICONS.closeCircleOutline} width={20} />, title: "Error" },
  info:    { color: "var(--accent-text)",  icon: <Iconify icon={UI_ICONS.notification} width={20} />, title: "Info" },
}

// Matches --ease token
const EASE = [0.32, 0.72, 0.36, 1.0] as const

function ToastItem({ toast, onRemove }: { toast: ToastType; onRemove: (id: number) => void }) {
  const cfg = VARIANT_CONFIG[toast.variant] ?? VARIANT_CONFIG.info

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0, transition: { duration: 0.22, ease: EASE } }}
      exit={{ opacity: 0, x: 24, transition: { duration: 0.22, ease: EASE } }}
      layout
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "12px",
          minWidth: 300,
          maxWidth: 400,
          mb: "8px",
          p: "14px 16px",
          borderRadius: "var(--r-md)",
          border: "1px solid var(--line)",
          borderLeftColor: cfg.color,
          borderLeftWidth: "3px",
          backgroundColor: "var(--bg-canvas)",
          boxShadow: "var(--shadow-2)",
          position: "relative",
        }}
      >
        <Box sx={{ color: cfg.color, flexShrink: 0, mt: "1px" }}>
          {cfg.icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pr: toast.undoFn ? "8px" : "20px" }}>
          <Typography
            sx={{
              fontFamily: "var(--font-body)",
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "var(--text-primary)",
              lineHeight: 1.3,
              mb: toast.msg ? "2px" : 0,
            }}
          >
            {toast.title}
          </Typography>
          {toast.msg && (
            <Typography
              sx={{
                fontFamily: "var(--font-body)",
                fontSize: "0.8125rem",
                color: "var(--text-primary)",
                lineHeight: 1.4,
              }}
            >
              {typeof toast.msg === "string" ? toast.msg : String(toast.msg ?? "")}
            </Typography>
          )}
        </Box>

        {toast.undoFn ? (
          <Box
            component="button"
            onClick={toast.undoFn}
            sx={{
              flexShrink: 0,
              alignSelf: "center",
              background: "none",
              border: "none",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "var(--accent-text)",
              px: "8px",
              py: "4px",
              borderRadius: "var(--r-sm)",
              "&:hover": { backgroundColor: "var(--accent-bg)" },
              transition: "background-color var(--t-fast) var(--ease)",
            }}
          >
            Undo
          </Box>
        ) : (
          <IconButton
            size="small"
            onClick={() => onRemove(toast.id)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 20,
              height: 20,
              opacity: 0.4,
              "&:hover": { opacity: 1 },
              transition: "opacity var(--t-fast) var(--ease)",
            }}
          >
            <Iconify icon={UI_ICONS.close} width={14} />
          </IconButton>
        )}
      </Box>
    </motion.div>
  )
}

export default function CustomToast({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Array<ToastType>>([])

  const remove = useCallback((id: number) => {
    setToasts(ts => ts.filter(t => t.id !== id))
  }, [])

  const toaster = useMemo<ToastContextValue>(() => ({
    ...BASE_TOAST_CONFIG,
    toast(msg, variant, title) {
      const id = Date.now() + Math.random()
      setToasts(ts => [...ts, {
        id,
        msg,
        variant: variant as ToastVariant,
        title: title || VARIANT_CONFIG[variant as ToastVariant]?.title || "Notification",
      }])
      setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 4000)
    },
    withUndo(msg, onUndo, opts) {
      const id = Date.now() + Math.random()
      let undone = false
      const handleUndo = () => {
        if (undone) return
        undone = true
        onUndo()
        setToasts(ts => ts.filter(t => t.id !== id))
      }
      setToasts(ts => [...ts, {
        id,
        msg,
        variant: "info",
        title: opts?.title || "Done",
        undoFn: handleUndo,
      }])
      setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 5500)
    },
  }), [])

  return (
    <ToastContext.Provider value={toaster}>
      {children}
      <Box
        sx={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          zIndex: 2000,      // var(--z-toast) — toast always wins
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          pointerEvents: "none",
          "& > *": { pointerEvents: "auto" },
        }}
      >
        <AnimatePresence>
          {toasts.map(t => (
            <ToastItem key={t.id} toast={t} onRemove={remove} />
          ))}
        </AnimatePresence>
      </Box>
    </ToastContext.Provider>
  )
}
